import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import type { SubmissionView } from '@/api/types';
import { StatusChip } from '@/components/StatusChip';
import { ErrorView } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';
import { COMPANY_NAMES } from '@/components/CompanyLogo';

const PAGE_SIZE = 30;

export function HistoryPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<SubmissionView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    let off = false;
    api.listSubmissions(0, PAGE_SIZE)
      .then((p) => { if (!off) { setItems(p.content); setTotal(p.totalElements); } })
      .catch((e) => { if (!off) setErr(e); });
    return () => { off = true; };
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = await api.listSubmissions(page + 1, PAGE_SIZE);
      setItems((prev) => [...(prev ?? []), ...next.content]);
      setTotal(next.totalElements);
      setPage(page + 1);
    } catch (e) {
      setErr(e);
    } finally {
      setLoadingMore(false);
    }
  };

  if (err) return <div className="page"><ErrorView error={err} /></div>;
  if (!items) return <FullPageSpinner />;

  if (items.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">История смен</h1>
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 48 }}>📭</div>
          <div style={{ marginTop: 8 }}>Пока нет загруженных смен.</div>
          <div className="page-subtitle" style={{ marginTop: 4 }}>
            Откройте «Главную» и загрузите первый отчёт.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">История смен</h1>
      {items.map((s) => (
        <button
          key={s.id}
          type="button"
          className="card"
          onClick={() => nav(`/submissions/${s.id}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            border: 'none', cursor: 'pointer', color: 'inherit', width: '100%',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{COMPANY_NAMES[s.company]}</div>
            <div style={{ color: 'var(--bk-hint)', fontSize: 13, marginTop: 2 }}>
              {new Date(s.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              {s.payoutRub != null && ` · ${s.payoutRub.toLocaleString('ru-RU')} ₽`}
            </div>
          </div>
          <StatusChip status={s.status} />
        </button>
      ))}
      {items.length < total && (
        <button
          type="button"
          className="big-cta secondary"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Загружаем…' : `Показать ещё (${total - items.length})`}
        </button>
      )}
    </div>
  );
}
